/**
 * Archetype → physical body: skeleton plan and material composition.
 *
 * This lives in `entity/` rather than in `meta/archetypes.ts` deliberately.
 * The meta layer must not import `sim/` (it is the one module in the project
 * with no dependencies on the simulation, which is what let it be built and
 * tested in isolation), and material ids are a `sim/` concept. So meta owns
 * what a body *does*, and this file owns what a body *is made of*.
 *
 * The composition is not decoration. It is the same field enemies use, so a
 * player archetype inherits the entire derived stack for free: a Possessed
 * Armor made of `STEEL` conducts electricity and rusts in acid; a Bone Construct
 * made of `BONE` goes brittle when frozen and shatters; a Spectral Entity made
 * of `VOID_MATTER` shrugs off matter and burns in radiance. Choosing a body
 * chooses a set of vulnerabilities, which is what docs/02 §3.2 means by "a body
 * is not just a skin."
 */

import { MAT, type MaterialName } from '../sim/materialData';
import type { MatId } from '../sim/materials';
import { own } from './plans/planText';

export interface ArchetypeBody {
  /** Skeleton plan id from pixelBody.ts. */
  plan: string;
  /** Up to 4 (material, fraction) pairs, most significant first. */
  composition: Array<[MaterialName, number]>;
  /** Capsule half-extents, in cells. Bodies are genuinely different sizes. */
  halfW: number;
  halfH: number;
}

const BODIES: Record<string, ArchetypeBody> = {
  // ---------------------------------------------------------- humanoid
  duelist: {
    plan: 'humanoid', halfW: 5, halfH: 11,
    composition: [['FLESH', 0.6], ['BONE', 0.3], ['STEEL', 0.1]],
  },
  breaker: {
    // Heavy and wide: the silhouette should read as mass before you fight it.
    plan: 'humanoid', halfW: 7, halfH: 12,
    composition: [['IRON', 0.5], ['FLESH', 0.35], ['BONE', 0.15]],
  },
  scavenger: {
    plan: 'humanoid', halfW: 4, halfH: 10,
    composition: [['FLESH', 0.55], ['CHITIN', 0.3], ['RUSTED_IRON', 0.15]],
  },
  hunter: {
    plan: 'humanoid', halfW: 4, halfH: 11,
    composition: [['FLESH', 0.6], ['CHITIN', 0.25], ['BONE', 0.15]],
  },
  'techno-knight': {
    plan: 'humanoid', halfW: 6, halfH: 12,
    composition: [['PLASTEEL', 0.5], ['CIRCUIT_PLATE', 0.3], ['FLESH', 0.2]],
  },
  ritualist: {
    plan: 'humanoid', halfW: 5, halfH: 11,
    composition: [['FLESH', 0.45], ['CURSED_BLOOD', 0.35], ['RUNESTONE', 0.2]],
  },
  berserker: {
    plan: 'humanoid', halfW: 6, halfH: 12,
    composition: [['FLESH', 0.65], ['BLOOD', 0.25], ['BONE', 0.1]],
  },
  'chrono-monk': {
    plan: 'humanoid', halfW: 4, halfH: 11,
    composition: [['FLESH', 0.5], ['CHRONO_FLUID', 0.3], ['CRYSTAL', 0.2]],
  },

  // ------------------------------------------------------------- weird
  'spectral-entity': {
    // Void body: immune to most matter, annihilated by radiance.
    plan: 'floater', halfW: 5, halfH: 10,
    composition: [['VOID_MATTER', 0.7], ['CURSED_RESIDUE', 0.3]],
  },
  'floating-relic': {
    plan: 'floater', halfW: 5, halfH: 9,
    composition: [['BRASS', 0.5], ['RUNESTONE', 0.3], ['CRYSTAL', 0.2]],
  },
  'parasite-colony': {
    plan: 'blob', halfW: 5, halfH: 8,
    composition: [['BIO_SLUDGE', 0.5], ['FLESH', 0.3], ['CHITIN', 0.2]],
  },
  'semi-liquid-mutant': {
    plan: 'blob', halfW: 6, halfH: 8,
    composition: [['BIO_SLUDGE', 0.6], ['PLAGUE_SLURRY', 0.25], ['FLESH', 0.15]],
  },
  'possessed-armor': {
    // No flesh at all: cannot drown, cannot swim, conducts beautifully.
    plan: 'humanoid', halfW: 6, halfH: 12,
    composition: [['STEEL', 0.7], ['CURSED_RESIDUE', 0.2], ['BONE', 0.1]],
  },
  'segmented-biomech': {
    // Its own plan, not `quadruped` — issue #186: `quadruped` is
    // `planNameFor`'s generic pick for every beast-proportioned enemy, and a
    // tail added there would have grown one behind soot_gremlin and
    // cinder_wretch too. See `plans/segmented.plan`'s own header.
    plan: 'segmented', halfW: 8, halfH: 8,
    composition: [['CHITIN', 0.45], ['PLASTEEL', 0.35], ['FLESH', 0.2]],
  },
  'bone-construct': {
    // Freeze it and it shatters — the derived weakness, not an authored one.
    plan: 'tower', halfW: 5, halfH: 14,
    composition: [['BONE', 0.75], ['GRAVE_SOIL', 0.15], ['RUNESTONE', 0.1]],
  },
  'modular-drone-swarm': {
    plan: 'floater', halfW: 5, halfH: 8,
    composition: [['PLASTEEL', 0.45], ['CIRCUIT_PLATE', 0.4], ['NANITE_DUST', 0.15]],
  },
  'flesh-machine-hybrid': {
    plan: 'humanoid', halfW: 6, halfH: 11,
    composition: [['FLESH', 0.4], ['CIRCUIT_PLATE', 0.35], ['MACHINE_OIL', 0.25]],
  },
};

const FALLBACK: ArchetypeBody = BODIES.duelist;

/**
 * `own`, and not `?? FALLBACK`, for the reason `profileAt` states: **an object
 * index is not total.** `BODIES['toString']` is a *function*, which is not
 * nullish, so the coalescing form never reached the fallback for any inherited
 * name and handed back `Function.prototype.toString` typed as an
 * `ArchetypeBody`. Every caller then read `.plan`, `.halfW`, `.halfH` and
 * `.composition` off it and got `undefined` for all four — and `compositionIds`
 * would have thrown iterating the last one.
 *
 * The parameter is a plain `string` and `archetypeBodyIds()` returns
 * `string[]`, so the compiler offers nothing at the call site. This is safe
 * today only because `tools/view/body.mjs` happens to source its keys from
 * `archetypeBodyIds()` — an accident that stops holding the moment someone
 * adds a caller, which is exactly how the other three were found.
 *
 * Fourth live instance in this codebase after `profileAt`/`profileOffsetAt`
 * and `visualForClass` (`src/entity/weaponVisual.ts`) and
 * `planFor`/`tissueByName` (`src/entity/pixelBody.ts`). The pattern is the
 * defect, not the line; `tests/pixelTools.test.ts` §6 walks all of them
 * together for that reason.
 */
export function bodyForArchetype(id: string): ArchetypeBody {
  return own(BODIES, id) ?? FALLBACK;
}

export function archetypeBodyIds(): string[] {
  return Object.keys(BODIES);
}

/**
 * Resolve a composition to material ids, padded to the 4 ECS slots.
 *
 * **The second table index in this file, checked because the trap is the
 * pattern and not the line.** `MAT` is keyed by `MaterialName`, so an inherited
 * name cannot arrive through the type — but `MAT` is also *empty until
 * `registerMaterials()` has run*, and in that window every lookup is
 * `undefined`. Unguarded, that pushed `undefined` into a `MatId[]` and the
 * body came out made of material `NaN`: a composition that silently means
 * nothing, which is worse than one that fails. `own` makes both cases the same
 * loud one.
 */
export function compositionIds(body: ArchetypeBody): {
  mats: MatId[];
  fracs: number[];
  core: MatId;
} {
  const mats: MatId[] = [];
  const fracs: number[] = [];
  for (const [name, frac] of body.composition) {
    const id = own(MAT as Readonly<Record<string, MatId>>, name);
    if (id === undefined) {
      throw new Error(
        `archetypeBody: material "${name}" is not registered — ` +
          'call registerMaterials() before resolving a composition',
      );
    }
    mats.push(id);
    fracs.push(frac);
  }
  while (mats.length < 4) {
    mats.push(0);
    fracs.push(0);
  }
  return { mats, fracs, core: mats[0] };
}
